import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from './useCompanyId';
import { db } from '../firebase';
import { doc, onSnapshot, collection, getDocs } from 'firebase/firestore';
import { findAccountIdByEmail } from '../services/firestoreService';
import { getInternalUsers } from '../services/userService';
import {
  computeStrategyCallUsage,
  findStrategyCallDeliverableContext,
} from '../utils/accountProgramDeliverables';

const PROGRAM_STATUS_MAP = {
  Lite: 'Lite',
  Plus: 'Plus',
  Pro: 'Pro',
  Hourly: 'Hourly',
  '5 Hours': '5 Hours',
  '10 Hours': '10 Hours',
  'A La Carte': 'A La Carte',
};

/**
 * Shared hook for Monthly 1:1 Strategy Calls usage.
 * Computes company-wide usage and limit for the current billing period
 * based on the client's program deliverables.
 */
export const useStrategyCalls = () => {
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  const deliverableContextRef = useRef({ programStatus: null, context: null });

  const [state, setState] = useState({
    used: null,
    limit: null,
    loading: true,
    periodDisplay: null,
    periodEnd: null,
    periodKey: null,
    periodStart: null,
    successManager: null,
  });

  useEffect(() => {
    let active = true;
    let unsubscribe = null;

    const resolveSuccessManager = async (account) => {
      if (account?.successManagerId) {
        try {
          const users = await getInternalUsers();
          const sm = users.find((u) => u.id === account.successManagerId);
          if (sm) {
            return {
              name: sm.name,
              profileImage: sm.profileImage,
              initials: sm.initials || (sm.name || 'SM').substring(0, 2).toUpperCase(),
            };
          }
        } catch {
          // ignore
        }
      }
      if (account?.successManager) {
        return {
          name: account.successManager,
          profileImage: null,
          initials: (account.successManager || 'SM').substring(0, 2).toUpperCase(),
        };
      }
      return null;
    };

    const loadDeliverableContext = async (account, programStatus) => {
      if (
        deliverableContextRef.current.programStatus === programStatus &&
        deliverableContextRef.current.context
      ) {
        return deliverableContextRef.current.context;
      }

      const programId = PROGRAM_STATUS_MAP[programStatus] || programStatus;
      const [contractItemsSnap, alaCarteSnap] = await Promise.all([
        getDocs(collection(db, 'programs', programId, 'contractItems')),
        getDocs(collection(db, 'alaCarteItems')),
      ]);

      const contractItems = contractItemsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const itemsData = alaCarteSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const context = findStrategyCallDeliverableContext(
        account,
        contractItems,
        itemsData,
        programStatus
      );

      deliverableContextRef.current = { programStatus, context };
      return context;
    };

    const hydrateFromAccount = async (account) => {
      const successManager = await resolveSuccessManager(account);
      const programStatus = account?.program?.status;

      if (!programStatus || programStatus === 'Not Started') {
        if (!active) return;
        setState({
          used: 0,
          limit: 1,
          loading: false,
          successManager,
          periodDisplay: null,
          periodEnd: null,
        });
        return;
      }

      const context = await loadDeliverableContext(account, programStatus);
      const usage = computeStrategyCallUsage(account, null, context);

      if (!active) return;

      if (!usage) {
        setState({
          used: 0,
          limit: 1,
          loading: false,
          successManager,
          periodDisplay: null,
          periodEnd: null,
        });
        return;
      }

      setState({
        used: usage.used,
        limit: usage.limit,
        loading: false,
        successManager,
        periodDisplay: usage.periodDisplay,
        periodEnd: usage.periodEnd,
        periodKey: usage.billingPeriod?.periodKey || null,
        periodStart: usage.billingPeriod?.periodStart || null,
      });
    };

    const setup = async () => {
      if (!currentUser?.email && !companyId) {
        setState({
          used: null,
          limit: null,
          loading: false,
          successManager: null,
          periodDisplay: null,
          periodEnd: null,
        });
        return;
      }

      const accountId =
        companyId ||
        (currentUser?.email ? await findAccountIdByEmail(currentUser.email, 'Clients') : null);

      if (!active) return;

      if (!accountId) {
        setState({
          used: 0,
          limit: 1,
          loading: false,
          successManager: null,
          periodDisplay: null,
          periodEnd: null,
        });
        return;
      }

      unsubscribe = onSnapshot(
        doc(db, 'accounts', accountId),
        (snap) => {
          if (!snap.exists()) return;
          hydrateFromAccount({ id: snap.id, ...snap.data() }).catch((err) => {
            console.error('Error loading strategy calls from deliverables:', err);
            if (!active) return;
            setState({
              used: 0,
              limit: 1,
              loading: false,
              successManager: null,
              periodDisplay: null,
              periodEnd: null,
            });
          });
        },
        (err) => {
          console.error('Error subscribing to strategy call account:', err);
          if (!active) return;
          setState({
            used: 0,
            limit: 1,
            loading: false,
            successManager: null,
            periodDisplay: null,
            periodEnd: null,
          });
        }
      );
    };

    setup();

    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser?.email, companyId]);

  const { used, limit, loading, periodDisplay, periodEnd, periodKey, periodStart, successManager } = state;

  const percentage =
    used != null && limit != null && limit > 0
      ? Math.min(100, Math.round((used / limit) * 100))
      : null;

  const atLimit = used != null && limit != null && used >= limit;

  return {
    used,
    limit,
    loading,
    percentage,
    atLimit,
    periodDisplay,
    periodEnd,
    periodKey: periodKey || null,
    periodStart: periodStart || null,
    successManager,
  };
};
